import { Link } from "react-router-dom";
import useSocialMeta from "../hooks/useSocialMeta";
import "./MusicalIntelligenceLearnMore.css";

/* Musical Intelligence™
 * ---------------------------------------------------------------------
 * Full brand regeneration on the Liturgical Intelligence™ canonical
 * system. Cathedral chrome for the hero, parchment for the workspace,
 * bronze reserved for the one primary CTA.
 *
 * Named image placeholders (drop-in, no structural redesign needed):
 *   musical-intelligence-hero   → sacred-window (stained glass)
 *   sacred-window               → same photograph, used as material
 *   altar-frontal-band          → bronze altar frontal, bounded band
 *   repertoire-detail           → reserved slot; drops in via one file swap
 * =================================================================== */

// Section anchors — preserved so any inbound deep-link still resolves.
const SECTIONS = [
  { id: "knows",       title: "What Musical Intelligence™ knows" },
  { id: "repertoire",  title: "Repertoire, structured" },
  { id: "discernment", title: "Editorial discernment" },
  { id: "memory",      title: "Parish Musical Memory™" },
  { id: "authority",   title: "Human authority" },
  { id: "ecosystem",   title: "Where it lives on the platform" },
];

const KNOWS = [
  { k: "Liturgical season and occasion",  d: "Advent · Christmas · Lent · Triduum · Easter · Ordinary Time · Solemnity · Funeral · Wedding." },
  { k: "Ritual placement",                 d: "Entrance · Kyrie · Gloria · Psalm · Gospel Acclamation · Preparation · Communion · Recessional · Postlude." },
  { k: "Scripture and theological theme",  d: "The appointed readings of the day, and the theological hinge each hymn text is built around." },
  { k: "Denomination and tradition",       d: "Roman Catholic, Episcopal, Anglican, Lutheran, Methodist, Presbyterian — with their own repertoires and rubrics." },
  { k: "Parish repertoire",                d: "Every hymn the parish has actually sung, and the ones its clergy have quietly ruled out." },
  { k: "Hymnal resources",                 d: "The specific books this parish uses, edition and page number preserved with the recommendation." },
  { k: "Assembly strength",                d: "How much the congregation can sustain — melodically and rhythmically — on any given Sunday." },
  { k: "Choir capability",                 d: "SATB voicing, soloist ranges, current rehearsal load." },
  { k: "Musical difficulty",               d: "The organist's read of a piece, not a stock difficulty rating." },
  { k: "Organ and instrumental resources", d: "Pipe organ, digital, piano only, brass on feast days — the plan respects the room's actual instrument." },
  { k: "Parish Memory™",                   d: "What was sung last Advent, what worked, what the assembly quietly refused to sing." },
];

const REPERTOIRE_FIELDS = [
  "title", "tune", "composer", "source · hymnal · edition · page",
  "liturgical moments", "seasons", "theological themes",
  "musical character", "assembly strength", "choir usage",
  "difficulty (organist's read)", "denominational appropriateness",
];

const DISCERNMENT_QUESTIONS = [
  { q: "Why this piece?",           a: "The text lands on the day's readings, the tune fits the assembly's voice, and the moment in the rite calls for exactly this weight." },
  { q: "Why here?",                  a: "Its placement — Entrance, Preparation, Communion, Recessional — is not interchangeable. The rite has a shape; the music serves it." },
  { q: "Why for this congregation?", a: "This parish's actual repertoire, this choir's voicing, this organist's instrument, this pastor's discretion." },
  { q: "Why in this season?",         a: "Advent restraint, Christmas incarnate, Lenten silence, Easter proclamation — each has its own weight and its own permissions." },
  { q: "What should be confirmed?",   a: "Anything the platform is not certain of — a rubric interpretation, a solo policy, a family request — is surfaced for the musician and clergy to resolve." },
];

const MEMORY_ITEMS = [
  "What has been sung — every service, every season",
  "What the congregation now carries by heart",
  "What worked · warm reception, sung with confidence",
  "What proved difficult · noted for the next rehearsal cycle",
  "What the choir learned this season — anthems now in repertoire",
  "Seasonal repertoire history — five years of Advent, ten years of Easter",
  "Parish-specific preferences — the pastor's rulings, the community's texts",
];

const ECOSYSTEM = [
  { p: "SundayBuilder™",   d: "Uses Musical Intelligence™ during weekly preparation — a recommendation appears for every placement, with reasoning." },
  { p: "Annual Planner™",  d: "Uses it across seasons — the whole year is composed as a musical arc, not one Sunday at a time." },
  { p: "Guest Ministry™",  d: "Uses it with host-parish confirmation and denomination-aware tradition safeguards — invented titles are downgraded before they reach the organist." },
  { p: "Parish Memory™",   d: "Strengthens future recommendations by remembering what actually happened in the sanctuary." },
  { p: "Ordo AI™",         d: "Supports the reasoning layer. Does not define repertoire authority — that remains with the musician, the clergy, and the tradition." },
];


export default function MusicalIntelligenceLearnMore() {
  useSocialMeta({
    title: "Musical Intelligence™ — Sacred music, understood in context",
    description:
      "The repertoire and discernment system for the musical life of the Church — bringing liturgical placement, congregational context, tradition, and pastoral reasoning into one intelligent musical workspace.",
    path: "/musical-intelligence",
  });

  return (
    <div className="mi-lm-page" data-testid="mi-learn-more">
      {/* ============ HERO · full-bleed sanctuary, edge to edge ========== */}
      <header className="mi-hero" data-testid="mi-hero">
        {/* Sanctuary photograph as architecture — scaled beyond cover so
            the visitor is inside the room, not shown a picture of it. The
            image sits behind everything and bleeds under the altar band. */}
        <div
          className="mi-hero-sanctuary"
          role="presentation"
          aria-hidden="true"
          style={{ backgroundImage: 'url("/brand/musical-intelligence/sacred-window.webp")' }}
        />
        {/* Ambient illumination — a very low-opacity warm radial centered
            on the stained glass. Sanctuary reads as naturally lit, not as
            a photograph. */}
        <div className="mi-hero-glow" role="presentation" aria-hidden="true" />
        {/* Gradient veil — darkens the left nave for the copy, dissolves
            to nothing on the right so the stained glass reads clean. */}
        <div className="mi-hero-veil" role="presentation" aria-hidden="true" />

        <div className="mi-hero-inner">
          <div className="mi-hero-copy" data-testid="musical-intelligence-hero">
            <Link to="/" className="mi-back" data-testid="mi-lm-back">← Liturgical Intelligence™</Link>
            <div className="mi-eyebrow">A Liturgical Intelligence™ module</div>
            <h1 className="mi-h1">Musical Intelligence™</h1>
            <div className="mi-lede">Sacred music, understood in context.</div>
            <p className="mi-support">
              A repertoire and discernment system for the musical life of the
              Church — bringing liturgical placement, congregational context,
              tradition, repertoire knowledge, and pastoral reasoning into one
              intelligent musical workspace.
            </p>
            <div className="mi-cta-row">
              <Link to="/register" className="mi-cta" data-testid="mi-lm-cta">
                Explore Musical Intelligence™
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ============ Altar-frontal band — overlaps hero by ~28px ======== */}
      <div className="mi-altar-band" data-testid="altar-frontal-band" role="presentation" aria-hidden="true">
        <img
          className="mi-altar-band-img"
          src="/brand/musical-intelligence/altar-frontal-mi.webp"
          alt=""
        />
        <div className="mi-altar-band-veil" />
      </div>

      {/* ============ Section navigation — below the architecture ======== */}
      <nav className="mi-toc-bar" data-testid="mi-lm-toc" aria-label="Section navigation">
        <div className="mi-toc-bar-inner">
          {SECTIONS.map(s => (
            <a href={`#${s.id}`} key={s.id} className="mi-toc-bar-link">{s.title}</a>
          ))}
        </div>
      </nav>

      {/* ============ 1 · What Musical Intelligence Knows ================= */}
      <section id="knows" className="mi-section" data-testid="mi-lm-knows">
        <div className="mi-section-inner">
          <div className="mi-section-head">
            <div className="mi-section-eyebrow">01 · Context</div>
            <h2 className="mi-h2">What Musical Intelligence™ knows</h2>
            <p className="mi-section-lede">
              The right hymn is a function of many variables — season, ritual
              placement, assembly, choir, room, and history. Musical
              Intelligence™ holds all of them at once, so the musician doesn't
              have to.
            </p>
          </div>
          <ul className="mi-knows-list">
            {KNOWS.map(({ k, d }) => (
              <li key={k} className="mi-knows-item">
                <div className="mi-knows-k">{k}</div>
                <div className="mi-knows-d">{d}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 2 · Repertoire Intelligence ======================== */}
      <section id="repertoire" className="mi-section mi-section--tint" data-testid="mi-lm-repertoire">
        <div className="mi-section-inner mi-section-inner--split">
          <div className="mi-section-copy">
            <div className="mi-section-eyebrow">02 · Structure</div>
            <h2 className="mi-h2">Repertoire, structured — not generated.</h2>
            <p className="mi-section-lede">
              Every piece the platform recommends is a real hymn, from a real
              source, with a real place in the rite. Musical Intelligence™ is a
              structured repertoire — not a language model guessing at hymn
              titles.
            </p>
            <ul className="mi-fields">
              {REPERTOIRE_FIELDS.map(f => (
                <li key={f} className="mi-field-chip">{f}</li>
              ))}
            </ul>
          </div>
          <figure className="mi-repertoire-figure" data-testid="repertoire-detail">
            {/* Reserved slot for the repertoire-detail image. The panel
                renders cleanly until an image is dropped in. */}
            <div className="mi-repertoire-placeholder">
              <span className="mi-repertoire-placeholder-label">Repertoire detail</span>
            </div>
          </figure>
        </div>
      </section>

      {/* ============ 3 · Editorial Discernment (Rule 0.2) ================ */}
      <section id="discernment" className="mi-section" data-testid="mi-lm-discernment">
        <div className="mi-section-inner">
          <div className="mi-section-head">
            <div className="mi-section-eyebrow">03 · Reasoning</div>
            <h2 className="mi-h2">Every recommendation is explainable.</h2>
            <p className="mi-section-lede">
              Musical Intelligence™ does not produce answers. It produces
              recommendations, each of which answers five questions before the
              musician even opens the file.
            </p>
          </div>
          <ol className="mi-discernment">
            {DISCERNMENT_QUESTIONS.map(({ q, a }, i) => (
              <li key={q} className="mi-discernment-item">
                <div className="mi-discernment-idx">{String(i + 1).padStart(2, "0")}</div>
                <div className="mi-discernment-q">{q}</div>
                <div className="mi-discernment-a">{a}</div>
              </li>
            ))}
          </ol>
          <p className="mi-note">
            When any of these questions cannot be answered from the record, the
            recommendation is marked <em>awaiting confirmation</em> and the
            musician is asked — not shown a fabricated answer.
          </p>
        </div>
      </section>

      {/* ============ 4 · Parish Musical Memory ========================== */}
      <section id="memory" className="mi-section mi-section--tint" data-testid="mi-lm-memory">
        <div className="mi-section-inner">
          <div className="mi-section-head">
            <div className="mi-section-eyebrow">04 · Memory</div>
            <h2 className="mi-h2">Parish Musical Memory™</h2>
            <p className="mi-section-lede">
              Musical Intelligence™ becomes more useful the longer a parish
              uses it. It remembers only what has been recorded — not what it
              assumes. Nothing is invented.
            </p>
          </div>
          <ul className="mi-memory">
            {MEMORY_ITEMS.map(t => (
              <li key={t} className="mi-memory-item">{t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ 5 · Human Authority ================================ */}
      <section id="authority" className="mi-section mi-section--dark" data-testid="mi-lm-authority">
        <div className="mi-section-inner mi-section-inner--narrow">
          <div className="mi-section-eyebrow mi-section-eyebrow--dark">05 · Authority</div>
          <h2 className="mi-h2 mi-h2--dark">Musical Intelligence™ recommends. The musician decides.</h2>
          <p className="mi-support mi-support--dark">
            The platform exists to support the professional and pastoral
            judgment of the music director, organist, cantor, and clergy — not
            to replace it. Every recommendation is a starting frame. The final
            selection belongs to the person who knows this room, this choir,
            this Sunday, and this congregation.
          </p>
        </div>
      </section>

      {/* ============ 6 · Ecosystem ====================================== */}
      <section id="ecosystem" className="mi-section" data-testid="mi-lm-ecosystem">
        <div className="mi-section-inner">
          <div className="mi-section-head">
            <div className="mi-section-eyebrow">06 · Where it lives</div>
            <h2 className="mi-h2">Where Musical Intelligence™ lives on the platform</h2>
            <p className="mi-section-lede">
              Musical Intelligence™ is not a page you visit. It is the
              reasoning layer inside every workspace where music has to be
              chosen, planned, or remembered.
            </p>
          </div>
          <ul className="mi-ecosystem">
            {ECOSYSTEM.map(({ p, d }) => (
              <li key={p} className="mi-ecosystem-item">
                <div className="mi-ecosystem-p">{p}</div>
                <div className="mi-ecosystem-d">{d}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ CTA — one primary bronze button ==================== */}
      <section className="mi-cta-section" data-testid="mi-lm-cta-section">
        <div className="mi-section-inner mi-section-inner--narrow">
          <h2 className="mi-h2">Start with a single Sunday.</h2>
          <p className="mi-support">
            The best introduction to Musical Intelligence™ is one week of
            worship prepared inside SundayBuilder™. Nothing else.
          </p>
          <div className="mi-cta-row">
            <Link to="/register" className="mi-cta" data-testid="mi-lm-cta-final">
              Start using SundayBuilder™
            </Link>
            <Link to="/" className="mi-cta-secondary">Back to Liturgical Intelligence™</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
